/** The irreversible one. Deliberately hard to reach for, and it may name what is lost. */

import { useEffect, useState, type ReactNode } from "react";

import { Button } from "../Button/Button";
import { Dialog } from "../Dialog/Dialog";
import { Field } from "../Field/Field";
import { Input } from "../Input/Input";

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** Typed back before the action unlocks. */
  readonly confirmPhrase?: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly loading?: boolean;
  readonly width?: string | number;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly children?: ReactNode;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmPhrase,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  loading = false,
  width,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  const locked = confirmPhrase !== undefined && typed.trim() !== confirmPhrase;

  return (
    <Dialog
      open={open}
      title={title}
      onClose={onCancel}
      {...(description === undefined ? {} : { description })}
      {...(width === undefined ? {} : { width })}
      actions={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={locked} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {confirmPhrase !== undefined ? (
        <Field label={`Type ${confirmPhrase}`}>
          <Input mono value={typed} onChange={(event) => setTyped(event.target.value)} />
        </Field>
      ) : null}
    </Dialog>
  );
}
